import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  User,
  ShoppingBag,
  Languages,
  Bell,
  HelpCircle,
  Info,
  Shield,
  LogOut,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { api } from "../../store/slices/authSlice";
import styles from "./ProfileMenu.module.css";

const BASE_URL = "https://api.rawkemya.com/api";

export default function ProfileMenu() {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const currentLang = i18n.language?.startsWith("ar") ? "ar" : "en";

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get("/profile");
        if (res.data.code === 200) setProfile(res.data.data);
      } catch (err) {
        console.error("Profile fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleLanguageToggle = () => {
    const next = currentLang === "ar" ? "en" : "ar";
    i18n.changeLanguage(next);
    localStorage.setItem("lang", next);
    document.documentElement.dir = next === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = next;
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        await axios.post(
          `${BASE_URL}/logout`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }
    } catch (err) {
      console.error("Logout error:", err);
    }
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  return (
    <div className={styles.page} dir={isRtl ? "rtl" : "ltr"}>
      <div className={styles.hero}>
        <div className={styles.avatarCircle}>
          {profile?.image ? (
            <img
              src={profile.image}
              alt="avatar"
              className={styles.avatarImg}
            />
          ) : (
            <User size={40} strokeWidth={1.2} color="#9ca3af" />
          )}
        </div>
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.nameSkeleton} />
        ) : (
          <div className={styles.nameBlock}>
            <h2 className={styles.name}>{profile?.name || "—"}</h2>
            <p className={styles.email}>{profile?.email || "—"}</p>
          </div>
        )}

        <SectionLabel label={t("profile.personalDataSection")} />
        <div className={styles.group}>
          <MenuItem
            icon={<User size={18} />}
            label={t("profile.myData")}
            onClick={() => navigate("/profile")}
            isRtl={isRtl}
          />
          <Divider />
          <MenuItem
            icon={<ShoppingBag size={18} />}
            label={t("profile.ordersHistory")}
            onClick={() => navigate("/orders")}
            isRtl={isRtl}
          />
        </div>

        <SectionLabel label={t("profile.settingsSection")} />
        <div className={styles.group}>
          <MenuItem
            icon={<Languages size={18} />}
            label={t("profile.language")}
            value={currentLang === "ar" ? "العربية" : "English"}
            onClick={handleLanguageToggle}
            isRtl={isRtl}
          />
          <Divider />
          <MenuItem
            icon={<Bell size={18} />}
            label={t("profile.notifications")}
            onClick={() => navigate("/notifications")}
            isRtl={isRtl}
          />
        </div>

        <SectionLabel label={t("profile.helpSection")} />
        <div className={styles.group}>
          <MenuItem
            icon={<HelpCircle size={18} />}
            label={t("profile.help")}
            onClick={() => navigate("/help")}
            isRtl={isRtl}
          />
          <Divider />
          <MenuItem
            icon={<Info size={18} />}
            label={t("profile.about")}
            onClick={() => navigate("/about")}
            isRtl={isRtl}
          />
          <Divider />
          <MenuItem
            icon={<Shield size={18} />}
            label={t("profile.terms")}
            onClick={() => navigate("/terms")}
            isRtl={isRtl}
          />
        </div>

        <div className={`${styles.group} ${styles.logoutGroup}`}>
          <MenuItem
            icon={<LogOut size={18} color="#ef4444" />}
            label={t("profile.logout")}
            labelStyle={{ color: "#ef4444" }}
            onClick={handleLogout}
            hideChevron
            isRtl={isRtl}
          />
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ label }) {
  return <p className={styles.sectionLabel}>{label}</p>;
}

function Divider() {
  return <div className={styles.divider} />;
}

function MenuItem({
  icon,
  label,
  value,
  onClick,
  labelStyle,
  hideChevron,
  isRtl,
}) {
  return (
    <button className={styles.menuItem} onClick={onClick}>
      <span className={styles.menuIcon}>{icon}</span>
      <span className={styles.menuLabel} style={labelStyle}>
        {label}
      </span>
      {value && <span className={styles.menuValue}>{value}</span>}
      {!hideChevron &&
        (isRtl ? (
          <ChevronLeft size={16} className={styles.menuChevron} />
        ) : (
          <ChevronRight size={16} className={styles.menuChevron} />
        ))}
    </button>
  );
}
